import { useEffect, useRef, useCallback } from 'react';

import { TContext } from '../../../types/context';
import { BlinkingTitleSingleton } from '../../../utils/BlinkingTitleSingleton/BlinkingTitleSingleton';
import { getTotalUnreadMessagesCount } from '../../services/getTotalUnreadMessagesCount';
import { useThrottle } from '../useThrottle';

/**
 * Хук устанавливает обработчик события visibilitychange и включает мигающий заголовок (количество не прочитанных сообщений).
 * @param context TContext
 */
export const useBlinkingTitle = (context: TContext) => {
  const blinkingTitle = useRef<BlinkingTitleSingleton | null>(null);
  /** RS включен ли мигающий заголовок */
  const isBlinkingTitleEnabled = context.config.get<boolean>('frontend-chats.blinkingTitle.Enabled');

  const getUnreadMessages = useCallback(async () => {
    if (document.visibilityState === 'visible') {
      return;
    }

    const unreadMessageCount = await getTotalUnreadMessagesCount(context);
    if (unreadMessageCount > 0) {
      blinkingTitle.current?.setBlinkingDocumentInfo(unreadMessageCount);
    } else {
      blinkingTitle.current?.resetDocumentInfo();
    }
  }, [context]);

  const { start, cancel } = useThrottle(getUnreadMessages);

  const setBlinkingDocumentTitle = useCallback(() => {
    blinkingTitle.current = BlinkingTitleSingleton.getInstance();
    start();
  }, [start]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setBlinkingDocumentTitle();
      } else {
        blinkingTitle.current?.resetDocumentInfo();
        cancel();
      }
    };

    if (isBlinkingTitleEnabled) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (isBlinkingTitleEnabled) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [cancel, isBlinkingTitleEnabled, setBlinkingDocumentTitle, start]);
};
